import { getDocumentAsync } from 'expo-document-picker'
import AntDesign from '@expo/vector-icons/AntDesign'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { View, Text } from 'react-native'
import { Button } from '@/src/common/components/Button'
import { SlideInModal } from '@/src/common/components/SlideInModal'
import { type ComponentRef, forwardRef, useImperativeHandle, useRef } from 'react'
import * as ImagePicker from 'expo-image-picker'
import { mesaColorNames, typography } from '@/src/common/theme'

export const DEFAULT_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'text/html',
]

type DocumentUploadHandler = (file: { uri: string; mimeType: string; name: string }) => void

export const UploadModal = forwardRef<
  ComponentRef<typeof SlideInModal>,
  {
    title: string
    onUploadFile?: DocumentUploadHandler
    onUploadPhoto?: DocumentUploadHandler
    onUploadLibrary?: DocumentUploadHandler
    fileTypes?: string[]
  }
>(
  (
    { title, onUploadFile, onUploadPhoto, onUploadLibrary, fileTypes = DEFAULT_FILE_TYPES },
    ref,
  ) => {
    const Alert = useAlert('Document Upload')
    const bottomSheetRef = useRef<ComponentRef<typeof SlideInModal>>(null)
    useImperativeHandle(ref, () => bottomSheetRef.current ?? undefined)

    const imageResponseHandler = ({
      result,
      handler,
    }: {
      result: ImagePicker.ImagePickerResult
      handler?: DocumentUploadHandler
    }) => {
      if (result.assets?.length) {
        if (result.assets.length === 1) {
          const image = result.assets[0]
          if (image) {
            const { uri, mimeType, fileName } = image

            if (mimeType && fileName) {
              handler?.({ uri, mimeType, name: fileName })
              bottomSheetRef.current?.close()
            } else {
              Alert.alert('We are having issues uploading this image from your library')
              return
            }
          }
        } else {
          Alert.alert('You can only select 1 image at a time')
        }
      }
    }

    const uploadFile = async (fileTypes: string[]) => {
      try {
        const { assets } = await getDocumentAsync({
          type: fileTypes,
          copyToCacheDirectory: true,
          multiple: false,
        })
        const asset = assets?.[0]
        if (!asset) return

        const { uri, mimeType, name } = asset

        if (!uri || !mimeType || !fileTypes.includes(mimeType)) {
          Alert.alert(`${name} is not a valid document`)
          return
        }

        onUploadFile?.({ uri, mimeType, name })

        bottomSheetRef.current?.close()
      } catch {
        Alert.alert('Error picking document')
      }
    }

    const uploadImageFromLibrary = async () => {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: 'images',
        quality: 1,
        allowsMultipleSelection: false,
      })

      imageResponseHandler({ result, handler: onUploadLibrary })
    }

    const onUploadPhotofromCameraRoll = async () => {
      const cameraRollStatus = await ImagePicker.requestMediaLibraryPermissionsAsync()
      const cameraStatus = await ImagePicker.requestCameraPermissionsAsync()

      if (
        cameraRollStatus.status === ImagePicker.PermissionStatus.GRANTED ||
        cameraStatus.status === ImagePicker.PermissionStatus.GRANTED
      ) {
        const result = await ImagePicker.launchCameraAsync({
          mediaTypes: 'images',
          quality: 1,
          allowsMultipleSelection: false,
        })

        imageResponseHandler({ result, handler: onUploadPhoto })
      } else {
        Alert.alert('Sorry, we need these permissions so you can upload your documents')
      }
    }

    return (
      <SlideInModal ref={bottomSheetRef} showClose={false}>
        <View style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 24 }}>
          <View style={{ paddingHorizontal: 16, gap: 10 }}>
            <Text
              style={[
                { color: mesaColorNames['Greyscale/900 #152438'], textAlign: 'center' },
                typography['heading/heading3'],
              ]}
            >
              {title}
            </Text>
          </View>
          <View style={{ paddingHorizontal: 16, gap: 10 }}>
            <Text
              style={[
                { color: mesaColorNames['Greyscale/900 #152438'], textAlign: 'center' },
                typography.S,
              ]}
            >
              Select how you want to upload your document
            </Text>
          </View>
          {onUploadPhoto && (
            <Button
              onPress={() => void onUploadPhotofromCameraRoll()}
              icon={
                <AntDesign
                  name="camera"
                  size={24}
                  color={mesaColorNames['Greyscale/900 #152438']}
                  caption="Document uploaded"
                  style={{ marginRight: 8 }}
                />
              }
              title="Take a photo"
            />
          )}
          {onUploadLibrary && (
            <Button
              onPress={() => void uploadImageFromLibrary()}
              icon={
                <AntDesign
                  name="appstore-o"
                  size={24}
                  color={mesaColorNames['Greyscale/900 #152438']}
                  caption="Document uploaded"
                  style={{ marginRight: 8 }}
                />
              }
              title="Choose from Library"
            />
          )}
          {onUploadFile && (
            <Button
              onPress={() => void uploadFile(fileTypes)}
              icon={
                <AntDesign
                  name="folderopen"
                  size={24}
                  color={mesaColorNames['Greyscale/900 #152438']}
                  caption="Document uploaded"
                  style={{ marginRight: 8 }}
                />
              }
              title="Choose from Files"
            />
          )}
        </View>
      </SlideInModal>
    )
  },
)
